import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, GitBranch, FlaskConical, TrendingDown } from 'lucide-react';
import PageBanner from '../components/common/PageBanner';
import { Section, Head, Card, Index, SideImage } from '../components/common/Primitives';
import { decisionLayer } from '../data/system';
import banner from '../assets/banners/dosing.jpg';
import agronomy from '../assets/tech/agronomy-analysis.jpg';

const d = decisionLayer;

const Dosing = () => (
  <div>
    <PageBanner
      image={banner}
      kicker="Farm Tech · Layer 04"
      title="Decision & dosing"
      lead={d.intro}
    />

    {/* Condition monitor - the trigger */}
    <Section tone="soil" tight>
      <div className="grid lg:grid-cols-[auto_1fr] gap-6 items-start">
        <p className="label text-lime shrink-0 lg:pt-1">Condition monitor</p>
        <p className="text-paper text-lg md:text-[1.35rem] leading-relaxed font-display">
          {d.conditionMonitor}
        </p>
      </div>
    </Section>

    {/* Two engines */}
    <Section tone="paper">
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-8 flex flex-col">
          <div className="flex items-center gap-3">
            <Index n={1} />
            <p className="label text-forest">The decider</p>
          </div>
          <h2 className="mt-4 font-display font-semibold text-[1.6rem] text-ink leading-tight">
            {d.decisionModel.title}
          </h2>
          <ul className="mt-6 space-y-3.5">
            {d.decisionModel.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-forest mt-1 shrink-0" />
                <span className="text-soil/75 text-[14.5px] leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
          <p className="mt-auto pt-7">
            <span className="block rounded-lg bg-ink text-paper/80 font-mono text-[12.5px] leading-relaxed p-4">
              {d.decisionModel.io}
            </span>
          </p>
        </Card>

        <Card className="p-8 flex flex-col">
          <div className="flex items-center gap-3">
            <Index n={2} accent />
            <p className="label text-amber">Requirement to physical dose</p>
          </div>
          <h2 className="mt-4 font-display font-semibold text-[1.6rem] text-ink leading-tight">
            {d.recipeEngine.title}
          </h2>
          <ul className="mt-6 space-y-3.5">
            {d.recipeEngine.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-amber mt-1 shrink-0" />
                <span className="text-soil/75 text-[14.5px] leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
          <p className="mt-auto pt-7">
            <span className="block rounded-lg bg-amber/10 ring-1 ring-amber/25 text-soil/80 text-[13.5px] leading-relaxed p-4">
              {d.recipeEngine.close}
            </span>
          </p>
        </Card>
      </div>
    </Section>

    {/* The shape of the problem, not the method.

        This section used to print the dosing relation and then walk the five
        steps that apply it, which is the one genuinely copyable thing on the
        page. It now states the problem as three stages and describes the
        solver from the outside (inputs, obligations, output) so a reader
        can see how much the system is holding together without being handed
        the step that does it. */}
    <Section tone="ink" className="fieldgrid">
      <div className="max-w-4xl">
        <p className="label text-lime flex items-center gap-2">
          <FlaskConical className="w-3.5 h-3.5" /> The dosing problem
        </p>
        <p className="mt-6 font-mono text-[1.35rem] md:text-[2rem] leading-snug text-paper">
          <span className="text-lime">{d.solve.stages[0]}</span>
          <span className="text-paper/30"> → </span>
          <span className="text-data">{d.solve.stages[1]}</span>
          <span className="text-paper/30"> → </span>
          <span className="text-amber">{d.solve.stages[2]}</span>
        </p>
        <p className="mt-5 text-paper/50 text-[15px] italic">{d.solve.note}</p>
      </div>

      <div className="mt-14 grid lg:grid-cols-2 gap-6">
        {/* The solver, described from the outside */}
        <div className="rounded-xl bg-white/[0.04] ring-1 ring-white/10 p-8 flex flex-col">
          <p className="label text-lime flex items-center gap-2">
            <GitBranch className="w-3.5 h-3.5" /> What it is handed
          </p>
          <ul className="mt-7 space-y-3.5">
            {d.given.map((g) => (
              <li key={g} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-lime/70 mt-2 shrink-0" />
                <span className="text-paper/70 text-[14.5px] leading-relaxed">{g}</span>
              </li>
            ))}
          </ul>

          <p className="label text-amber mt-9">What it has to hand back</p>
          <ul className="mt-6 space-y-3.5">
            {d.returns.map((r) => (
              <li key={r} className="flex items-start gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber/70 mt-2 shrink-0" />
                <span className="text-paper/70 text-[14.5px] leading-relaxed">{r}</span>
              </li>
            ))}
          </ul>

          <p className="mt-auto pt-9 text-paper/45 text-[13.5px] leading-relaxed">
            {d.solveClose}
          </p>
        </div>

        {/* Worked example */}
        <div className="rounded-xl bg-amber/[0.07] ring-1 ring-amber/30 p-8">
          <p className="label text-amber">Every dose must satisfy all six</p>
          <dl className="mt-7">
            {d.verify.map((w) => (
              <div
                key={w.k}
                className="grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-1 sm:gap-4 py-3.5 border-b border-white/10"
              >
                <dt className="label text-amber/90">{w.k}</dt>
                <dd className="text-paper/75 text-[14.5px] leading-snug">{w.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </Section>

    {/* Round down */}
    <Section tone="paper">
      <div className="grid lg:grid-cols-2 gap-14 items-center">
        <SideImage
          src={agronomy}
          alt="An agronomist recording crop measurements against pH and nutrient readings"
        />
        <div>
          <Head
            eyebrow="The conservative bias"
            title="When in doubt, it under-applies"
            lead="Between two valid recipes the engine always rounds down. Under-applying a nutrient is recoverable on the next cycle. Over-applying it is not."
          />
          <div className="mt-8 rounded-xl bg-chalk ring-1 ring-soil/10 p-6 flex items-start gap-4">
            <TrendingDown className="w-5 h-5 text-forest shrink-0 mt-0.5" />
            <p className="text-soil/75 text-[14.5px] leading-relaxed">{d.verifyNote}</p>
          </div>
          <Link
            to="/farm-tech/safety"
            className="inline-flex items-center gap-2 mt-8 text-forest hover:text-ink font-medium transition-colors"
          >
            See the gates that approve it
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Section>
  </div>
);

export default Dosing;
